import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { apiUrl } from '../utils/apiBase';
import CapWidget from '../components/CapWidget';

const SimpleLoginPage = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const emailLocale = (i18n.language || 'en').split('-')[0];
  const [step, setStep] = useState('email');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('land_reporter');
  const [verificationCode, setVerificationCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const pageTitle =
    role === 'land_reporter'
      ? t('auth.landReporter')
      : role === 'land_seeker'
        ? t('auth.landSeeker')
        : t('navigation.admin');

  const handleSendCode = async (e) => {
    e.preventDefault();
    console.log('Sending code for:', { email, role });
    
    setIsLoading(true);
    try {
      if (!captchaToken) {
        toast.error(t('auth.completeCaptcha'));
        setIsLoading(false);
        return;
      }
      const response = await fetch(apiUrl('/auth/send-verification'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, role, captcha: captchaToken, locale: emailLocale }),
      });

      const data = await response.json();
      console.log('Response:', data);
      
      if (data.success) {
        setStep('verify');
        toast.success(t('auth.codeSent'));
      } else {
        if (data.current_role && data.attempted_role) {
          const currentRole = data.current_role.replace(/_/g, ' ');
          const attemptedRole = data.attempted_role.replace(/_/g, ' ');
          toast.error(t('simpleLoginPage.alreadyRegisteredAsRole', { currentRole, attemptedRole }));
        } else {
          toast.error(data.message || t('loginPage.failedToSendCode'));
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('loginPage.failedToSendCode'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    console.log('Verifying code:', { email, verificationCode });
    
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl('/auth/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ email, verification_code: verificationCode }),
      });

      const data = await response.json();
      console.log('Verify response:', data);
      
      if (data.success) {
        localStorage.setItem('token', data.data.token);
        toast.success(t('loginPage.loginSuccess'));
        
        // Wait a moment for the token to be set
        setTimeout(async () => {
          if (role === 'land_reporter') {
            navigate('/report'); // Land reporters go directly to report page
          } else {
            // Check if user has active subscription
            try {
              const token = localStorage.getItem('token');
              const response = await fetch(apiUrl('/auth/me'), {
                headers: {
                  'Accept': 'application/json',
                  'Authorization': `Bearer ${token}`,
                },
              });
              
              if (response.ok) {
                const userData = await response.json();
                if (userData.data?.has_active_subscription) {
                  navigate('/search'); // Users with subscription go to search page
                } else {
                  navigate('/preview'); // Users without subscription go to preview page
                }
              } else {
                navigate('/preview'); // Fallback to preview page
              }
            } catch (error) {
              console.error('Error checking subscription status:', error);
              navigate('/preview'); // Fallback to preview page
            }
          }
        }, 1000);
      } else {
        if (data.current_role && data.attempted_role) {
          const currentRole = data.current_role.replace(/_/g, ' ');
          toast.error(t('simpleLoginPage.alreadyRegisteredCannotChange', { currentRole }));
        } else {
          toast.error(data.message || t('auth.invalidCode'));
        }
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('loginPage.failedToVerifyCode'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{pageTitle}</h1>
          <p className="text-gray-600">
            {step === 'email' ? t('simpleLoginPage.enterEmailTitle') : t('simpleLoginPage.enterVerificationTitle')}
          </p>
        </div>

        {step === 'email' ? (
          <form onSubmit={handleSendCode} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('loginPage.emailPlaceholder')}
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('simpleLoginPage.role')}
              </label>
              <select
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="land_reporter">{t('auth.landReporter')}</option>
                <option value="land_seeker">{t('auth.landSeeker')}</option>
              </select>
            </div>

            <div className="flex justify-center">
              <CapWidget
                siteKey="salesign-demo"
                onVerify={(token) => setCaptchaToken(token)}
                onError={(error) => {
                  console.error('Cap verification failed:', error);
                  setCaptchaToken(null);
                }}
                theme="light"
                size="normal"
              />
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? t('common.sending') : t('simpleLoginPage.sendCode')}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-gray-600">
                {t('simpleLoginPage.codeSentTo')} <strong>{email}</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.verificationCode')}
              </label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
                placeholder={t('loginPage.codePlaceholder')}
                maxLength="6"
                required
              />
            </div>

            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setStep('email')}
                className="flex-1 bg-gray-500 text-white py-3 rounded-lg hover:bg-gray-600"
              >
                {t('common.back')}
              </button>
              <button
                type="submit"
                disabled={isLoading || verificationCode.length !== 6}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isLoading ? t('loginPage.verifying') : t('simpleLoginPage.verify')}
              </button>
            </div>
          </form>
        )}

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            ← {t('loginPage.backToHome')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleLoginPage;
